/**
 * 设备报警配置 - 详情弹窗
 */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Modal, Form, Cascader, Button, message, Tag, Input, Card } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { jtDeviceAlarmTypeData } from '@/constants/jtDeviceAlarm';
import CarTree from '@/components/carTree';
import { createAlarmPush, deleteAlarmPush } from '@/service/jtAlarmConfig';
import { getAlarmTypeCodeFromValue, displayJtDeviceAlarmV2, getAlarmTypeValueFromCode } from '@/utils/jtDeviceAlarm';

export type ModalMode = 'create' | 'view' | 'edit';

interface DetailModalProps {
    visible: boolean;
    onClose: () => void;
    onSuccess?: () => void; // 新增成功回调
    record: any;
    mode: ModalMode;
    aggregationType?: string; // 聚合类型，用于判断数据来源
}

const DetailModal: React.FC<DetailModalProps> = ({
    visible,
    onClose,
    onSuccess,
    record,
    mode,
    aggregationType
}) => {
    const [form] = Form.useForm();
    const carTreeRef = useRef<any>(null);
    const [plateSearchText, setPlateSearchText] = useState('');
    const [selectedCarInfos, setSelectedCarInfos] = useState<any[]>([]); // 存储 CarTree 选中的车辆信息
    const [submitting, setSubmitting] = useState(false);
    
    // 编辑模式下的状态
    const [editedPlateList, setEditedPlateList] = useState<any[]>([]); // 编辑后的车辆列表
    const [editedAlarmTypes, setEditedAlarmTypes] = useState<string[][]>([]); // 编辑后的报警类型
    const [hasEdited, setHasEdited] = useState(false); // 是否有编辑过
    
    // 原始数据的id列表，用于删除
    const [originalIds, setOriginalIds] = useState<number[]>([]);
    
    const isCreate = mode === 'create';
    const isView = mode === 'view';
    const isEdit = mode === 'edit';
    
    const titleMap = {
        create: '新建',
        view: '查看详情',
        edit: '编辑'
    };
    const title = titleMap[mode];

    // 根据聚合类型获取报警类型列表
    const alarmTypeList = useMemo(() => {
        if (!record) return [];
        if (aggregationType === 'plate') {
            // 按车牌聚合：报警类型在 alarmTypeList 字段
            return record.alarmTypeList || [];
        } else if (aggregationType === 'alarmType') {
            // 按报警类型聚合：报警类型就是 record 本身
            return [{ alarmType: record.alarmType, warningType: record.warningType }];
        }
        return [];
    }, [record, aggregationType]);

    // 根据聚合类型获取车辆列表
    const plateList = useMemo(() => {
        if (!record) return [];
        if (aggregationType === 'plate') {
            // 按车牌聚合：只有一个车辆
            return [{ id: record.carId, plateNumber: record.plateNumber }];
        } else if (aggregationType === 'alarmType') {
            // 按报警类型聚合：车辆列表在 plateNumberList 字段
            return record.plateNumberList || [];
        }
        return [];
    }, [record, aggregationType]);

    // 编辑模式下的过滤（基于 editedPlateList）
    const displayPlateList = useMemo(() => {
        return isEdit ? editedPlateList : plateList;
    }, [isEdit, editedPlateList, plateList]);

    // 根据搜索文本过滤车牌号
    const filteredPlateList = useMemo(() => {
        const sourceList = displayPlateList;
        if (!plateSearchText.trim()) return sourceList;
        return sourceList.filter((item: any) => 
            item.plateNumber?.toLowerCase().includes(plateSearchText.toLowerCase())
        );
    }, [displayPlateList, plateSearchText]);

    // 弹窗关闭时重置状态
    useEffect(() => {
        if (!visible) {
            setPlateSearchText('');
            setEditedPlateList([]);
            setEditedAlarmTypes([]);
            setHasEdited(false);
            setOriginalIds([]);
        }
    }, [visible]);

    // 编辑模式下初始化数据
    useEffect(() => {
        if (visible && isEdit && record) {
            // 初始化编辑状态下的车辆列表
            setEditedPlateList([...plateList]);
            
            // 初始化编辑状态下的报警类型
            const alarmValues = alarmTypeList.map((item: any) => 
                getAlarmTypeValueFromCode({ alarmType: item.alarmType, warningType: item.warningType })
            );
            setEditedAlarmTypes(alarmValues);
            
            // 获取原始的id列表，用于删除
            let ids: number[] = [];
            if (aggregationType === 'plate') {
                ids = record.alarmTypeList?.map((item: any) => item.id).filter((id: number) => id !== undefined) || [];
            } else if (aggregationType === 'alarmType') {
                ids = record.plateNumberList?.map((item: any) => item.id).filter((id: number) => id !== undefined) || [];
            }
            setOriginalIds(ids);
            
            setHasEdited(false);
        }
    }, [visible, isEdit, record, plateList, alarmTypeList, aggregationType]);

    // 查看/编辑模式下加载数据
    useEffect(() => {
        if (visible && !isCreate && record) {
            // 设置表单值
            form.setFieldsValue({
                alarmTypes: record.alarmTypes || [],
                carIds: record.carIds || []
            });
        } else if (visible && isCreate) {
            // 新建模式清空表单
            form.resetFields();
        }
    }, [visible, isCreate, record, form]);

    // 获取选中的车辆信息
    const getSelectedCarInfos = () => {
        return selectedCarInfos;
    };

    // CarTree 勾选回调
    const handleCarTreeCheck = (carIds: number[], carInfos: any[]) => {
        setSelectedCarInfos(carInfos || []);
    };

    // 编辑模式下删除车辆
    const handleRemovePlate = (plateItem: any) => {
        const newList = editedPlateList.filter(item => item.id !== plateItem.id);
        setEditedPlateList(newList);
        setHasEdited(true);
    };

    // 编辑模式下报警类型变更
    const handleAlarmTypesChange = (values: any) => {
        setEditedAlarmTypes(values);
        setHasEdited(true);
    };

    // 确认按钮点击（新建模式）
    const handleCreateConfirm = async () => {
        try {
            // 验证表单
            await form.validateFields();

            const formValues = form.getFieldsValue();
            const cars = getSelectedCarInfos();
            // debugger;

            const alarmTypeListParams = formValues.alarmTypes?.map((item: string[]) => {
                const [alarmType, warningType] = getAlarmTypeCodeFromValue(item);
                return { alarmType, warningType };
            }) || [];

            const carList = cars.map((car: any) => ({
                id: car.metaId,
                plateNumber: car.title_base || car.title  // CarTree 组件中车辆的车牌号在 title_base 字段
            }));

            const params = {
                alarmTypeList: alarmTypeListParams,
                carList
            };

            const res = await createAlarmPush(params);

            if (res?.data?.success) {
                message.success('创建成功');
                onClose();
                onSuccess?.();
            } else {
                message.error(res?.data?.msg || '创建失败');
            }
        } catch (error: any) {
            console.error('创建失败:', error);
            if (error.errorFields) {
                // 表单验证失败
                return;
            }
            message.error('创建失败，请稍后重试');
        }
    };

    // 确认按钮点击（编辑模式）
    const handleEditConfirm = async () => {
        // 验证数据
        if (editedAlarmTypes.length === 0) {
            message.warning('请至少选择一个报警类型');
            return;
        }
        if (editedPlateList.length === 0) {
            message.warning('请至少保留一辆车');
            return;
        }

        setSubmitting(true);
        try {
            // 第一步：删除原有的所有配置
            if (originalIds.length > 0) {
                const deleteRes = await deleteAlarmPush(originalIds);
                if (!deleteRes?.data?.success) {
                    message.error(deleteRes?.data?.msg || '删除原配置失败');
                    setSubmitting(false);
                    return;
                }
            }

            // 第二步：创建新的配置
            const alarmTypeListParams = editedAlarmTypes.map((item: string[]) => {
                const [alarmType, warningType] = getAlarmTypeCodeFromValue(item);
                return { alarmType, warningType };
            });

            const carList = editedPlateList.map((item: any) => ({
                id: item.carId || item.id, // 根据数据结构可能是carId或id
                plateNumber: item.plateNumber
            }));

            const params = {
                alarmTypeList: alarmTypeListParams,
                carList
            };

            const createRes = await createAlarmPush(params);

            if (createRes?.data?.success) {
                message.success('保存成功');
                onClose();
                onSuccess?.();
            } else {
                message.error(createRes?.data?.msg || '保存失败');
            }
        } catch (error: any) {
            console.error('保存失败:', error);
            message.error('保存失败，请稍后重试');
        } finally {
            setSubmitting(false);
        }
    };

    // 确认按钮点击
    const handleConfirm = async () => {
        if (isCreate) {
            await handleCreateConfirm();
        } else if (isEdit) {
            await handleEditConfirm();
        }
    };

    // 取消按钮点击
    const handleCancel = () => {
        if (isEdit && hasEdited) {
            Modal.confirm({
                title: '提示',
                content: '当前更改未保存，是否确定退出？',
                okText: '确定',
                cancelText: '取消',
                onOk: () => {
                    onClose();
                }
            });
        } else {
            onClose();
        }
    };

    // 弹窗关闭事件（点击X按钮或遮罩层）
    const handleModalClose = () => {
        if (isEdit && hasEdited) {
            Modal.confirm({
                title: '提示',
                content: '当前更改未保存，是否确定退出？',
                okText: '确定',
                cancelText: '取消',
                onOk: () => {
                    onClose();
                }
            });
        } else {
            onClose();
        }
    };

    // 渲染查看模式的内容
    const renderViewContent = () => (
        <div>
            {/* 报警类型展示 */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>报警类型：</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {alarmTypeList.map((item: any, index: number) => (
                        <Tag key={`${item.alarmType}_${item.warningType}_${index}`} color="blue">
                            {displayJtDeviceAlarmV2(item)}
                        </Tag>
                    ))}
                </div>
            </div>
            
            {/* 车辆展示卡片 */}
            <Card 
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>关联车辆</span>
                        <Input
                            placeholder="搜索车牌号"
                            prefix={<SearchOutlined />}
                            value={plateSearchText}
                            onChange={(e) => setPlateSearchText(e.target.value)}
                            style={{ width: 200 }}
                            allowClear
                        />
                    </div>
                }
                size="small"
            >
                <div style={{ maxHeight: 300, overflow: 'auto' }}>
                    {filteredPlateList.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {filteredPlateList.map((item: any) => (
                                <Tag key={item.id} color="green">
                                    {item.plateNumber}
                                </Tag>
                            ))}
                        </div>
                    ) : (
                        <div style={{ color: '#999', textAlign: 'center' }}>
                            {plateSearchText ? '没有匹配的车牌号' : '暂无关联车辆'}
                        </div>
                    )}
                </div>
                {plateList.length > 0 && (
                    <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
                        共 {plateList.length} 辆车{plateSearchText && `，筛选显示 ${filteredPlateList.length} 辆`}
                    </div>
                )}
            </Card>
        </div>
    );

    // 渲染编辑模式的内容
    const renderEditContent = () => (
        <div>
            {/* 提示文字 */}
            <div style={{ marginBottom: 16, padding: '8px 12px', backgroundColor: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4, color: '#8c6e00' }}>
                编辑模式仅能修改报警类型以及车辆的去除，如果要添加车辆可以在创建预警提醒配置中进行添加
            </div>
            
            {/* 报警类型展示 - 可编辑 */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>报警类型：</div>
                <Cascader
                    multiple
                    options={jtDeviceAlarmTypeData}
                    maxTagCount="responsive"
                    allowClear
                    showCheckedStrategy={Cascader.SHOW_CHILD}
                    showSearch
                    placeholder="请选择报警类型"
                    value={editedAlarmTypes}
                    onChange={handleAlarmTypesChange}
                    style={{ width: '100%' }}
                />
            </div>
            
            {/* 车辆展示卡片 - 带删除按钮 */}
            <Card 
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>关联车辆</span>
                        <Input
                            placeholder="搜索车牌号"
                            prefix={<SearchOutlined />}
                            value={plateSearchText}
                            onChange={(e) => setPlateSearchText(e.target.value)}
                            style={{ width: 200 }}
                            allowClear
                        />
                    </div>
                }
                size="small"
            >
                <div style={{ maxHeight: 300, overflow: 'auto' }}>
                    {filteredPlateList.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {filteredPlateList.map((item: any) => (
                                <Tag 
                                    key={item.id} 
                                    color="green"
                                    closable
                                    onClose={(e) => {
                                        e.preventDefault();
                                        handleRemovePlate(item);
                                    }}
                                >
                                    {item.plateNumber}
                                </Tag>
                            ))}
                        </div>
                    ) : (
                        <div style={{ color: '#999', textAlign: 'center' }}>
                            {plateSearchText ? '没有匹配的车牌号' : '暂无关联车辆'}
                        </div>
                    )}
                </div>
                {displayPlateList.length > 0 && (
                    <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
                        共 {displayPlateList.length} 辆车{plateSearchText && `，筛选显示 ${filteredPlateList.length} 辆`}
                    </div>
                )}
            </Card>
        </div>
    );

    // 渲染创建模式的表单内容
    const renderCreateContent = () => (
        <Form
            form={form}
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
            disabled={isView}
        >
            <Form.Item
                label="报警类型"
                name="alarmTypes"
                rules={[{ required: true, message: '请选择报警类型' }]}
            >
                <Cascader
                    multiple
                    options={jtDeviceAlarmTypeData}
                    maxTagCount="responsive"
                    allowClear
                    showCheckedStrategy={Cascader.SHOW_CHILD}
                    showSearch
                    placeholder="请选择报警类型"
                />
            </Form.Item>
            <Form.Item
                label="选择车辆"
                name="carIds"
                rules={[
                    {
                        required: true,
                        validator: async (_, value) => {
                            const carInfos = getSelectedCarInfos();
                            if (!carInfos || carInfos.length === 0) {
                                return Promise.reject(new Error('请选择车辆'));
                            }
                            return Promise.resolve();
                        }
                    }
                ]}
            >
                <div style={{ height: 400, overflow: 'auto', border: '1px solid #d9d9d9', borderRadius: 4 }}>
                    <CarTree
                        ref={carTreeRef}
                        checkable
                        treeCheck={handleCarTreeCheck}
                        showCheckActions
                    />
                </div>
            </Form.Item>
        </Form>
    );

    // 获取footer
    const getFooter = () => {
        if (isView) {
            return (
                <Button onClick={handleCancel}>关闭</Button>
            );
        }
        if (isCreate || isEdit) {
            return (
                <div>
                    <Button onClick={handleCancel}>取消</Button>
                    <Button 
                        type="primary" 
                        onClick={handleConfirm} 
                        style={{ marginLeft: 8 }}
                        loading={submitting}
                    >
                        确定
                    </Button>
                </div>
            );
        }
        return null;
    };

    // 根据模式渲染内容
    const renderContent = () => {
        if (isView) {
            return renderViewContent();
        }
        if (isEdit) {
            return renderEditContent();
        }
        if (isCreate) {
            return renderCreateContent();
        }
        return null;
    };

    return (
        <Modal
            title={title}
            open={visible}
            onCancel={handleModalClose}
            footer={getFooter()}
            width={"40%"}
            destroyOnClose
        >
            {renderContent()}
        </Modal>
    );
};

export default DetailModal;
